/**
 * Per-city fact + FAQ clusters used by GoldCityAliasPage and SilverPage.
 * Purpose: give each big-city page ≥3 unique fact chips and ≥3 bespoke
 * FAQs so identical-template pages don't trip Google's duplicate-content
 * / helpful-content signals, and to inject the long-tail question
 * keywords (200+ from the master sheet) into the pages users actually
 * search for.
 */

import { extraGoldCityData } from "./city-facts-extra";

export type Fact = { label: string; value: string };
export type QA = { q: string; a: string };

type CityData = {
  facts: Fact[];
  faqs: QA[];
  /** Related internal links, city-specific (top 6) */
  related: { href: string; label: string }[];
};

/* ─── PAKISTAN GOLD CITIES ────────────────────────────────────────── */

const PK_GOLD: Record<string, CityData> = {
  karachi: {
    facts: [
      { label: "Bullion hub", value: "Sarafa Bazaar, Boulton Market" },
      { label: "Wedding season", value: "Oct – Feb (peak demand)" },
      { label: "Local premium", value: "PKR 3,500 – 4,500 / tola" },
      { label: "Hallmark", value: "PSQCA 916 / 999" },
      { label: "Popular karat", value: "22K & 21K jewellery" },
      { label: "Payment mode", value: "Cash + jewellery lay-away" },
    ],
    faqs: [
      { q: "Karachi mein aaj ka gold rate per tola kya hai 24K aur 22K?", a: "Karachi ka 24K aur 22K per-tola rate is page ke rate table mein har 10 minute par live update hota hai. Karachi Sarafa Bazaar Association ka daily rate isi international spot + USD/PKR + PKR 3,500–4,500 local premium ke qareeb hi announce hota hai." },
      { q: "Karachi mein gold ki cheapest jagah kaunsi hai?", a: "Sarafa Bazaar Bolton Market, Tariq Road aur Jodia Bazaar mein bulk-jeweller quotes usually 0.3–0.8% cheap milte hain retail malls ke muqable. Making charges 6–14% negotiate hoti hain — hallmark 916/999 confirm zaroor karo." },
      { q: "Kya Karachi mein gold sasta hai Lahore ke muqable?", a: "Karachi base rate hai — import ki port yehi hai. Lahore, Islamabad, Peshawar ka rate typically PKR 200–600/tola upar hota hai transport + local margin ke wajah se. 24K bullion ke liye Karachi 0.05–0.15% cheaper." },
    ],
    related: [
      { href: "/silver-rate-karachi", label: "Silver rate Karachi" },
      { href: "/gold-rate-lahore", label: "Gold rate Lahore" },
      { href: "/pakistan", label: "Gold rate Pakistan (all cities)" },
      { href: "/pakistan/22k", label: "22K gold rate Pakistan" },
      { href: "/pakistan/calculator", label: "Pakistan gold calculator" },
      { href: "/pakistan/zakat-nisab", label: "Zakat nisab (PKR)" },
    ],
  },
  lahore: {
    facts: [
      { label: "Bullion hub", value: "Anarkali · Suha Bazaar · Liberty" },
      { label: "Wedding season", value: "Nov – Feb (Punjabi shaadi peak)" },
      { label: "Local premium", value: "PKR 3,800 – 5,000 / tola" },
      { label: "Hallmark", value: "PSQCA 916 / 999" },
      { label: "Popular karat", value: "22K heavy sets, 21K daily-wear" },
      { label: "Nearest import route", value: "via Karachi port" },
    ],
    faqs: [
      { q: "Lahore mein aaj ka gold rate 1 tola 24K kitna hai?", a: "Lahore ka live 24K per-tola rate iss page ke rate-table mein har 10 minute par refresh ho raha hai. Anarkali Sarafa Bazaar aur Suha Bazaar dono roz subah 10 baje announcement karte hain isi international-spot base pe." },
      { q: "Lahore mein sabse acha jeweller area kaunsa hai?", a: "Anarkali Bazaar traditional pieces ke liye, Liberty Market designer/branded ke liye aur Suha Bazaar bulk 22K sets aur dowry jewellery ke liye best hai. Making charges 8–18% depending on design complexity." },
      { q: "Lahore vs Karachi gold rate mein farq kitna hai?", a: "Lahore ka rate typically Karachi se PKR 250–500 per tola upar hota hai — reason: transport, dealer margin aur Punjab ki demand pressure (shaadi season). International spot + USD/PKR component both cities mein same rehta hai." },
    ],
    related: [
      { href: "/silver-rate-lahore", label: "Silver rate Lahore" },
      { href: "/gold-rate-karachi", label: "Gold rate Karachi" },
      { href: "/pakistan", label: "Gold rate Pakistan (all cities)" },
      { href: "/pakistan/22k", label: "22K gold rate Pakistan" },
      { href: "/pakistan/calculator", label: "Pakistan gold calculator" },
      { href: "/pakistan/zakat-nisab", label: "Zakat nisab (PKR)" },
    ],
  },
};

/* ─── INDIA GOLD CITIES ───────────────────────────────────────────── */

const IN_GOLD: Record<string, CityData> = {
  delhi: {
    facts: [
      { label: "Bullion hub", value: "Karol Bagh · Chandni Chowk · Dariba Kalan" },
      { label: "Wedding season", value: "Nov – Feb + Apr – May" },
      { label: "Duty + GST", value: "12.5% + 3%" },
      { label: "Hallmark", value: "BIS HUID (24C / 22C / 18C)" },
      { label: "Popular karat", value: "22K bridal, 18K diamond-set" },
      { label: "IBJA reference", value: "AM/PM 999 rate" },
    ],
    faqs: [
      { q: "Delhi mein aaj ka 10 gram sone ka rate 24K aur 22K kya hai?", a: "Delhi ka live 24K aur 22K per-10-gram rate is page ke rate table mein har 10 minute par refresh hota hai. IBJA AM aur PM reference rate is same base pe publish hoti hai. Karol Bagh aur Chandni Chowk ke jewellers isi ke around 0.05% quote karte hain." },
      { q: "Delhi mein gold cheapest kahan milta hai — Karol Bagh ya Chandni Chowk?", a: "Chandni Chowk ka Dariba Kalan wholesale bullion aur coin ke liye tightest premium (0.5–1% over spot) offer karta hai. Karol Bagh designer jewellery aur bridal collections ke liye popular hai — making charge 8–20%. Both areas BIS-hallmarked pieces sell karte hain." },
      { q: "Delhi vs Mumbai gold rate mein difference kyun hai?", a: "Delhi base rate reference hoti hai; Mumbai typically ₹50–150/10g lower quote karta hai kyunki Zaveri Bazaar direct import hub hai. Chennai ₹150–300/10g upar rehta hai making + regional premium ke wajah se. International spot + USD/INR sab jagah same." },
    ],
    related: [
      { href: "/chandi-ka-rate-delhi", label: "Chandi ka rate Delhi" },
      { href: "/gold-rate-mumbai", label: "Gold rate Mumbai" },
      { href: "/india", label: "Gold rate India (all cities)" },
      { href: "/gold-purity-calculator", label: "Gold purity calculator" },
      { href: "/gold-investment-calculator", label: "Gold investment ROI" },
      { href: "/zakat-calculator", label: "Zakat calculator" },
    ],
  },
  mumbai: {
    facts: [
      { label: "Bullion hub", value: "Zaveri Bazaar · Bhuleshwar" },
      { label: "Wedding season", value: "Nov – Feb + Apr – May" },
      { label: "Duty + GST", value: "12.5% + 3%" },
      { label: "Hallmark", value: "BIS HUID mandatory" },
      { label: "Popular karat", value: "22K wedding, 18K modern" },
      { label: "MCX exchange", value: "Gold futures 09:00–23:30 IST" },
    ],
    faqs: [
      { q: "Mumbai mein aaj ka 24K aur 22K sone ka rate per 10 gram kitna hai?", a: "Mumbai ka live 24K aur 22K per-10-gram rate iss page ke rate table mein har 10 minute par refresh hota hai. Zaveri Bazaar Association ka daily rate is base ke qareeb hi announce hota hai — Mumbai typically national reference se ₹50–150/10g cheaper hoti hai import hub honi ke wajah se." },
      { q: "Mumbai Zaveri Bazaar mein gold buy karna safe hai?", a: "Zaveri Bazaar India ka sabse bada bullion market hai — 3000+ registered jewellers. BIS HUID hallmark aur pucca bill (GST invoice) ki demand karo. Bulk 24K coin/bar deals mein premium 0.4–0.8% over MCX spot milta hai." },
      { q: "Mumbai mein MCX gold aur physical rate mein farq kitna hai?", a: "MCX Gold futures spot ke bahut close trade hote hain (0.05–0.15% basis). Physical bar/coin retail purchase par 1–2% premium hota hai (Zaveri) plus 3% GST. Jewellery mein making charges (3–25%) sabse bada component hote hain." },
    ],
    related: [
      { href: "/silver-rate-mumbai", label: "Silver rate Mumbai" },
      { href: "/gold-rate-delhi", label: "Gold rate Delhi" },
      { href: "/india", label: "Gold rate India (all cities)" },
      { href: "/gold-purity-calculator", label: "Gold purity calculator" },
      { href: "/gold-investment-calculator", label: "Gold investment ROI" },
      { href: "/zakat-calculator", label: "Zakat calculator" },
    ],
  },
};

/* ─── SILVER CITIES (both PK+IN) ──────────────────────────────────── */

const SILVER_CITY: Record<string, CityData> = {
  karachi: {
    facts: [
      { label: "Bullion hub", value: "Sarafa Bazaar, Boulton Market" },
      { label: "Local premium", value: "PKR 80 – 120 per tola" },
      { label: "Popular unit", value: "Per tola & per kg" },
      { label: "Silver purity", value: "999 fine · 925 sterling" },
    ],
    faqs: [
      { q: "Karachi mein aaj chandi ka rate 1 tola kitna hai?", a: "Karachi ka live silver / chandi rate per-tola iss page mein har 10 minute par update hota hai — international XAG spot + USD/PKR + PKR 80–120 local premium ke hisaab se. Sarafa Bazaar daily announcement is base ke qareeb hi hoti hai." },
      { q: "Chandi Karachi mein 1 kg ki price kya hai?", a: "1 kg silver = per-gram rate × 1000. Per-gram figure iss page ke rate table mein PKR mein listed hai; bulk 1kg bar par usually 0.5–1% wholesale discount milta hai reputable Sarafa Bazaar dealers se." },
    ],
    related: [
      { href: "/gold-rate-karachi", label: "Gold rate Karachi" },
      { href: "/silver-rate-pakistan", label: "Silver rate Pakistan (all cities)" },
      { href: "/silver-rate-lahore", label: "Silver rate Lahore" },
      { href: "/pakistan", label: "Gold rate Pakistan" },
      { href: "/pakistan/calculator", label: "Pakistan gold/silver calculator" },
      { href: "/pakistan/zakat-nisab", label: "Silver zakat nisab" },
    ],
  },
  lahore: {
    facts: [
      { label: "Bullion hub", value: "Anarkali · Suha Bazaar" },
      { label: "Local premium", value: "PKR 90 – 130 per tola" },
      { label: "Popular unit", value: "Per tola" },
      { label: "Silver purity", value: "999 · 925 sterling" },
    ],
    faqs: [
      { q: "Lahore mein chandi ka rate aaj 1 tola kitna hai?", a: "Lahore ka live chandi rate per tola iss page mein har 10 minute par refresh ho raha hai. Anarkali Sarafa Association isi base ke qareeb roz announce karti hai — usually Karachi se PKR 10–30/tola upar." },
      { q: "Lahore mein pure silver kahan se buy karein?", a: "Anarkali aur Suha Bazaar ke registered Sarafa jewellers 999 fine bars, coins aur 925 sterling jewellery dono sell karte hain. Bill par 'PSQCA hallmark' aur weight (grams / tola) likha zaroor check karo." },
    ],
    related: [
      { href: "/gold-rate-lahore", label: "Gold rate Lahore" },
      { href: "/silver-rate-karachi", label: "Silver rate Karachi" },
      { href: "/silver-rate-pakistan", label: "Silver rate Pakistan (all cities)" },
      { href: "/pakistan", label: "Gold rate Pakistan" },
      { href: "/pakistan/calculator", label: "Pakistan calculator" },
      { href: "/pakistan/zakat-nisab", label: "Silver zakat nisab" },
    ],
  },
  mumbai: {
    facts: [
      { label: "Bullion hub", value: "Zaveri Bazaar · Bhuleshwar" },
      { label: "Local premium", value: "₹ 500 – 900 per kg" },
      { label: "Popular unit", value: "Per kg & per 10g" },
      { label: "Silver purity", value: "999 fine · 925 sterling" },
    ],
    faqs: [
      { q: "Mumbai mein chandi ka bhav aaj per kg kya hai?", a: "Mumbai ka live silver per-kg rate iss page ke rate table mein har 10 minute par refresh hota hai — international XAG spot + USD/INR + ₹500–900 local premium ke base pe. Zaveri Bazaar bullion desks isi ke qareeb quote karti hain." },
      { q: "Mumbai Zaveri Bazaar mein 1 tola silver ki price?", a: "1 tola = 11.6638 gram — per-tola rate iss page ke breakdown mein listed hai. Bulk deals (1kg+) par premium 0.3–0.6% wholesale discount ke saath milta hai reputable Zaveri Bazaar dealers se." },
    ],
    related: [
      { href: "/gold-rate-mumbai", label: "Gold rate Mumbai" },
      { href: "/silver-rate-india", label: "Silver rate India (all cities)" },
      { href: "/chandi-ka-rate-delhi", label: "Chandi ka rate Delhi" },
      { href: "/india", label: "Gold rate India" },
      { href: "/gold-purity-calculator", label: "Purity calculator" },
      { href: "/gold-investment-calculator", label: "Investment ROI" },
    ],
  },
  delhi: {
    facts: [
      { label: "Bullion hub", value: "Chandni Chowk · Karol Bagh" },
      { label: "Local premium", value: "₹ 600 – 1000 per kg" },
      { label: "Popular unit", value: "Per kg" },
      { label: "Silver purity", value: "999 fine · 925 sterling" },
    ],
    faqs: [
      { q: "Delhi mein aaj chandi ka rate per kg kitna hai?", a: "Delhi ka live silver per-kg bhav iss page mein har 10 minute par refresh ho raha hai — international XAG + USD/INR + ₹600–1000 local premium ke base pe. Chandni Chowk Dariba Kalan ke bullion dealers isi ke qareeb daily quote karte hain." },
      { q: "Delhi Karol Bagh mein silver jewellery cheapest kahan milti hai?", a: "Karol Bagh bulk 925 sterling ke liye tightest making (₹80–150/gram) offer karta hai. Chandni Chowk pure 999 bar/coin ke liye best — 0.4–0.8% over MCX spot par milte hain BIS-hallmarked pieces." },
    ],
    related: [
      { href: "/gold-rate-delhi", label: "Gold rate Delhi" },
      { href: "/silver-rate-india", label: "Silver rate India (all cities)" },
      { href: "/silver-rate-mumbai", label: "Silver rate Mumbai" },
      { href: "/india", label: "Gold rate India" },
      { href: "/gold-purity-calculator", label: "Purity calculator" },
      { href: "/gold-investment-calculator", label: "Investment ROI" },
    ],
  },
};

/* ─── PUBLIC API ──────────────────────────────────────────────────── */

export function goldCityData(country: "pakistan" | "india", slug: string): CityData | null {
  const src = country === "pakistan" ? PK_GOLD : IN_GOLD;
  // Cities without a hand-written cluster here fall through to the extended
  // set in city-facts-extra.ts, so no city page ships the generic template.
  return src[slug] ?? extraGoldCityData(country, slug) ?? null;
}

export function silverCityData(country: "pakistan" | "india", slug: string): CityData | null {
  return SILVER_CITY[slug] ?? null;
}

/**
 * Generic fallback fact strip when we have no bespoke data for a city.
 * Fills 3 chips derived from the city name so no city page ships empty.
 */
export function fallbackFacts(cityName: string, country: "pakistan" | "india", metal: "gold" | "silver"): Fact[] {
  const isPk = country === "pakistan";
  return [
    { label: "Bullion market", value: isPk ? `${cityName} Sarafa Bazaar` : `${cityName} Zaveri / bullion market` },
    { label: metal === "gold" ? "Popular karat" : "Silver purity", value: metal === "gold" ? "22K & 24K" : "999 fine · 925 sterling" },
    { label: "Refresh cadence", value: "Every 10 minutes" },
    { label: isPk ? "Wedding season" : "Wedding + festival season", value: isPk ? "Oct – Feb" : "Nov – Feb + Apr – May" },
  ];
}
